/**
 * Tier 4 (M21, docs/TOWERS_V2.md "Tier 4"): the run's branch choices and learned skills, the special card (Ascend or a
 * skill) that rides an in-run offer, and the two commands that spend it.
 *
 * **Per tower type, not per tower.** An Ascend card names a type (`ascend.<tower>`); choosing one of its two branches
 * commits that type to the branch for the rest of the run and lifts every tier-3 tower of it to tier 4 at once. Later
 * towers of the type reach tier 4 with an ordinary Upgrade card (tier 3 → 4 is open once the type has a branch). A skill
 * card (`skill.<tower>.<branch>.<a|b>`) teaches the skill to the type, so every tier-4 tower of it uses it. One decision
 * per type keeps the draft readable — "my Archers are Rangers" — and the card count small (12 Ascend + 48 skill cards),
 * where per-tower branches would ask the player to tell apart two Archers that look alike and multiply the skill cards
 * by the towers standing.
 */
import { BranchDef, SkillDef, ascendCardId, skillCardId } from "../../data/branches";
import { Fix64 } from "../../maths/fix64";
import { slowAtLevel } from "../levelGrowth";
import { CommandResult } from "../command";
import { Card, CardKind, OfferKind, Tower } from "../entities";
import { SimEventKind } from "../events";
import type { LevelSim } from "../levelSim";
import { promoteSquad } from "../barracks";

/** The tier a branch gives. */
export const ASCENDED_TIER = 4;
/** Tiers a tower type has in towers.json (1–3); the fourth is its branch's. */
export const BASE_TIERS = 3;

/** The run's tier-4 state (hashed once a branch is chosen: sim/branches/hash.ts). */
export class Ascension {
  /** Per tower archetype: the branch chosen this run (global branch index), or −1. */
  readonly branchOf: number[];
  /** Per skill (global index): learned this run. */
  readonly learned: boolean[];
  /** In-run offers shown since the last special card was taken (the special card's pace). */
  offersSinceSpecial = 0;
  /** Special cards dealt so far: which candidate the next one is (a rotation, no RNG). */
  specialsDealt = 0;
  /** A branch has been chosen: the tier-4 blocks join the state hash from here on. */
  active = false;
  constructor(towerCount: number, skillCount: number) {
    this.branchOf = Array.from({ length: towerCount }, () => -1);
    this.learned = Array.from({ length: skillCount }, () => false);
  }
}

/** The branch a tower fights as: its type's branch when it stands at tier 4, else null. */
export function branchOf(sim: LevelSim, t: Tower): BranchDef | null {
  if (t.tier !== ASCENDED_TIER) return null;
  const b = sim.ascension.branchOf[t.archetype];
  return b >= 0 ? sim.balance.branches.branches[b] : null;
}

/** The branch chosen for a tower type this run, or null. */
export function typeBranch(sim: LevelSim, archetype: number): BranchDef | null {
  const b = sim.ascension.branchOf[archetype] ?? -1;
  return b >= 0 ? sim.balance.branches.branches[b] : null;
}

/** The tower's skill in `slot` (0 = A, 1 = B) when it is tier 4 and its type has learned it, else null. */
export function skillOf(sim: LevelSim, t: Tower, slot: number): SkillDef | null {
  const b = branchOf(sim, t);
  if (b === null) return null;
  const s = b.skills[slot];
  return sim.ascension.learned[s.index] ? s : null;
}

/** Whether `t` fights as branch `id` (cheap: one array read and a string compare). */
export function isBranch(sim: LevelSim, t: Tower, id: string): BranchDef | null {
  const b = branchOf(sim, t);
  return b !== null && b.id === id ? b : null;
}

/** The skill named `id` on `t`, when it is tier 4 and learned. */
export function skill(sim: LevelSim, t: Tower, id: string): SkillDef | null {
  const b = branchOf(sim, t);
  if (b === null) return null;
  for (const s of b.skills) if (s.id === id && sim.ascension.learned[s.index]) return s;
  return null;
}

/** One tier's line for a type: tiers 1–3 from towers.json, tier 4 from the type's branch (falls back to tier 3). */
export interface TierLine { readonly damageMult: Fix64; readonly rangeBonus: Fix64; readonly soldierArmour: Fix64 }

export function tierLine(sim: LevelSim, archetype: number, tier: number): TierLine {
  const a = sim.balance.towers[archetype];
  if (tier <= a.tiers.length) return a.tiers[tier - 1];
  const top = a.tiers[a.tiers.length - 1];
  const b = typeBranch(sim, archetype);
  if (b === null) return top;
  return { damageMult: b.tier4.num("damageMult", top.damageMult), rangeBonus: b.tier4.num("rangeBonus", top.rangeBonus), soldierArmour: b.tier4.num("soldierArmour", top.soldierArmour) };
}

/**
 * A Frost hit's slow (fraction, ticks) at `tier`: its type's branch's harder freeze at tier 4 where the branch sets one
 * (the Glacier), else the archetype's; then grown by the type's tower level (sim/levelGrowth.ts). Combat's slow; the
 * Upgrade card previews the next tier with it.
 */
export function slowAtTier(sim: LevelSim, archetype: number, tier: number): readonly [Fix64, number] {
  const a = sim.balance.towers[archetype];
  const b = tier > BASE_TIERS ? typeBranch(sim, archetype) : null;
  const base: readonly [Fix64, number] = b === null || !b.tier4.has("slowFraction") ? [a.slowFraction, a.slowDurationTicks]
    : [b.tier4.num("slowFraction"), b.tier4.int("slowTicks", a.slowDurationTicks)];
  return slowAtLevel(a, sim.towerLevels[archetype] ?? 1, base);
}

/** The slow a tower's own aura lays on everything in range at `tier` (a Blizzard Shrine's `auraSlow`), zero without one. */
export function auraSlowAtTier(sim: LevelSim, archetype: number, tier: number): Fix64 {
  return tier > BASE_TIERS ? typeBranch(sim, archetype)?.tier4.num("auraSlow") ?? Fix64.Zero : Fix64.Zero;
}

/** The range a type's learned skills add at tier 4 (Eagle Eye's `rangeBonus`; zero otherwise). */
export function skillRangeBonus(sim: LevelSim, archetype: number): Fix64 {
  const b = typeBranch(sim, archetype);
  if (b === null) return Fix64.Zero;
  let r = Fix64.Zero;
  for (const s of b.skills) if (sim.ascension.learned[s.index] && s.id === "eagleEye") r = r.add(s.params.num("rangeBonus"));
  return r;
}

/** Whether `t` may take an Upgrade: below its type's top tier, or at tier 3 once its type has a branch. */
export function canRaise(sim: LevelSim, t: Tower): boolean {
  if (!t.alive) return false;
  const tiers = sim.balance.towers[t.archetype].tiers.length;
  if (t.tier < tiers) return true;
  return t.tier === BASE_TIERS && tiers === BASE_TIERS && (sim.ascension.branchOf[t.archetype] ?? -1) >= 0;
}

// ------------------------------------------------------------------ the special card

/**
 * The special cards the board could take now, in order: an Ascend card for each type with a tier-3 tower standing and
 * no branch yet (tower order), then each unlearned skill of a type with a tier-4 tower standing (A before B).
 */
export function specialCandidates(sim: LevelSim): Card[] {
  const data = sim.balance.branches;
  const out: Card[] = [];
  const t3 = new Set<number>(), t4 = new Set<number>();
  for (const t of sim.towerSlots) {
    if (!t.alive || sim.balance.towers[t.archetype].isBase) continue;
    if (t.tier === BASE_TIERS) t3.add(t.archetype);
    if (t.tier === ASCENDED_TIER) t4.add(t.archetype);
  }
  for (let i = 0; i < sim.balance.towers.length; i++) {
    if (t3.has(i) && (data.byTower[i]?.length ?? 0) === 2 && sim.ascension.branchOf[i] < 0) out.push({ kind: CardKind.Ascend, index: i });
  }
  for (let i = 0; i < sim.balance.towers.length; i++) {
    const b = typeBranch(sim, i);
    if (b === null || !t4.has(i)) continue;
    for (const s of b.skills) if (!sim.ascension.learned[s.index]) out.push({ kind: CardKind.Skill, index: s.index });
  }
  return out;
}

/** The first stage a special card may come on: the pacing's percent of the level's stages, rounded up, at least 2. */
export function specialFromStage(sim: LevelSim): number {
  const PERCENT = 100;
  // The run modes (docs/MODES.md): Endless has no stage count to take a share of (its cap is a safety number), so it
  // reads its own wave; Iron is one long wave, so the card may come in it once the pace allows.
  if (sim.isEndless) return sim.balance.branches.pacing.endlessFromWave;
  if (sim.isIron) return 1;
  return Math.max(2, Math.ceil((sim.stages * sim.balance.branches.pacing.fromStagePercent) / PERCENT));
}

/**
 * Called when an in-run offer shows, after its cards are dealt: counts the offer toward the special card's pace and, when
 * one is due and the board can take one, appends it as an extra card (after the hand's own cards, so the hand's plan and
 * draws are untouched). Deterministic: candidates in a fixed order, rotated by the specials dealt so far.
 */
export function dealSpecial(sim: LevelSim): void {
  const asc = sim.ascension, pacing = sim.balance.branches.pacing;
  const due = asc.offersSinceSpecial + 1 >= pacing.offerInterval;
  asc.offersSinceSpecial++;
  if (!due || sim.levelNumber < pacing.minLevel || sim.stage < specialFromStage(sim)) return;
  appendSpecial(sim);
}

/**
 * A rare rally offer (M36, sim/rares.ts): an Ascend or skill card rides it when the board can take one and the level
 * deals specials at all, outside the special card's own pace (which it neither waits for nor advances).
 */
export function dealSpecialRally(sim: LevelSim): void {
  if (sim.levelNumber < sim.balance.branches.pacing.minLevel) return;
  appendSpecial(sim);
}

/** A reroll redeals the hand: the special card it carried comes back (the same one: it is not part of the draw). */
export function redealSpecial(sim: LevelSim, carried: Card | null): void {
  if (carried === null) return;
  if (specialCandidates(sim).some((c) => c.kind === carried.kind && c.index === carried.index)) sim.offer.cards.push(carried);
}

function appendSpecial(sim: LevelSim): void {
  const list = specialCandidates(sim);
  if (list.length === 0) return;
  const card = list[sim.ascension.specialsDealt % list.length];
  sim.offer.cards.push(card);
  sim.ascension.specialsDealt++;
  sim.events.add(SimEventKind.SpecialDealt, sim.offer.id, card.kind, card.index);
}

/** The special card in the open offer's hand, or null. */
export function specialInHand(sim: LevelSim): Card | null {
  for (const c of sim.offer.cards) if (c.kind === CardKind.Ascend || c.kind === CardKind.Skill) return c;
  return null;
}

// ------------------------------------------------------------------ commands

/**
 * Spends the picked Ascend card: its type takes branch `slot` (0 or 1) for the run, and every tier-3 tower of it rises
 * to tier 4 (BranchChosen, then TowerAscended and TowerUpgraded per tower).
 */
export function chooseBranch(sim: LevelSim, slot: number, after: (t: Tower) => void, close: () => void): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  if (!sim.offer.hasPick) return CommandResult.NothingPicked;
  const card = sim.offer.pickedCard;
  if (card.kind !== CardKind.Ascend) return CommandResult.WrongCardKind;
  const pair = sim.balance.branches.byTower[card.index] ?? [];
  if (slot !== 0 && slot !== 1 || pair.length !== 2) return CommandResult.InvalidIndex;
  if (sim.ascension.branchOf[card.index] >= 0) return CommandResult.MaxTier;
  const b = sim.balance.branches.branches[pair[slot]];
  sim.ascension.branchOf[card.index] = b.index;
  sim.ascension.active = true;
  let raised = 0;
  for (const t of sim.towerSlots) if (t.alive && t.archetype === card.index && t.tier === BASE_TIERS) raised++;
  sim.events.add(SimEventKind.BranchChosen, card.index, b.index, raised);
  for (const t of sim.towerSlots) {
    if (!t.alive || t.archetype !== card.index || t.tier !== BASE_TIERS) continue;
    ascend(sim, t);
    after(t);
  }
  takeSpecial(sim, close);
  return CommandResult.Ok;
}

/** Spends the picked skill card: its type learns the skill for the run (SkillLearned). */
export function learnSkill(sim: LevelSim, close: () => void): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  if (!sim.offer.hasPick) return CommandResult.NothingPicked;
  const card = sim.offer.pickedCard;
  if (card.kind !== CardKind.Skill) return CommandResult.WrongCardKind;
  const s = sim.balance.branches.skills[card.index];
  if (s === undefined) return CommandResult.InvalidIndex;
  if (sim.ascension.learned[s.index]) return CommandResult.MaxTier;
  sim.ascension.learned[s.index] = true;
  sim.events.add(SimEventKind.SkillLearned, sim.balance.branches.branches[s.branchIndex].towerIndex, s.index);
  takeSpecial(sim, close);
  return CommandResult.Ok;
}

/** A tower rises to tier 4 (by the Ascend card or an Upgrade card on a type with a branch). */
export function ascend(sim: LevelSim, t: Tower): void {
  t.tier = ASCENDED_TIER;
  resetBranchState(t);
  sim.events.add(SimEventKind.TowerAscended, t.id, sim.ascension.branchOf[t.archetype]);
  sim.events.add(SimEventKind.TowerUpgraded, t.id, t.tier);
}

function takeSpecial(sim: LevelSim, close: () => void): void {
  sim.ascension.offersSinceSpecial = 0;
  if (sim.offer.kind === OfferKind.PostWave) {
    const cost = sim.balance.cards.pickPointsCost.upgrade;
    if (cost > 0) sim.nextOfferAt += cost;
  }
  close();
}

/**
 * Dev only: commit tower type `archetype` to its branch `slot` (0 or 1) with the given skills learned, and lift every
 * tier-3-or-higher tower of it to tier 4 — the captures, the balance tools and the tests stage a tier-4 tower without
 * playing to an Ascend card. The other branch's skills are forgotten. The caller recomputes the auras.
 */
export function debugSetBranch(sim: LevelSim, archetype: number, slot: number, skillA: boolean, skillB: boolean): void {
  const pair = sim.balance.branches.byTower[archetype] ?? [];
  if (pair.length !== 2) return;
  const b = sim.balance.branches.branches[pair[slot === 1 ? 1 : 0]];
  sim.ascension.branchOf[archetype] = b.index;
  sim.ascension.active = true;
  for (const i of pair) for (const k of sim.balance.branches.branches[i].skills) sim.ascension.learned[k.index] = false;
  sim.ascension.learned[b.skills[0].index] = skillA;
  sim.ascension.learned[b.skills[1].index] = skillB;
  for (const t of sim.towerSlots) if (t.alive && t.archetype === archetype && t.tier >= BASE_TIERS) { t.tier = BASE_TIERS; ascend(sim, t); promoteSquad(sim, t); }
}

/** Clears a tower's tier-4 timers and counters (on placement and on ascending). */
export function resetBranchState(t: Tower): void {
  t.shotCount = 0; t.skillTimer = 0; t.skillActive = 0; t.charge = 0; t.burstLeft = 0; t.zoneTimer = 0;
  t.extraBeams.length = 0;
  t.auraHaste = Fix64.Zero; t.rallyHaste = Fix64.Zero; t.auraRangeBonus = Fix64.Zero;
}

/** The card id a card shows (`ascend.<tower>`, `skill.<tower>.<branch>.<a|b>`), or null for road, tower and upgrade cards. */
export function specialCardId(sim: LevelSim, card: Card): string | null {
  if (card.kind === CardKind.Ascend) return ascendCardId(sim.balance.towers[card.index].id);
  if (card.kind === CardKind.Skill) return skillCardId(sim.balance.branches, sim.balance.branches.skills[card.index]);
  return null;
}
